///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Component } from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { findIndex, times } from "lodash";

import PanelStateIndicator from "../PanelStateIndicator";
import { status, userRole, mode as modeTypes } from "../../../helpers/types";
import Toolbar from "./Toolbar";
import PricingGridHeader from "./PricingGridHeader";
import {
  changePricingLanesPagination,
  getLaneMatchCountForTLLanes,
  refetchRxoTlPricingPage,
  setColumnOrder,
  toggleColumnFilterMenu,
  toggleRxoTlColumnFilterMenu,
  filterPricingLanes,
  setColumnWidth,
  sortPricingLanes,
  handleSpecialPaste
} from "../../../slices/bidPricingSlice";
import Pagination from "../../form-controls/Pagination";
import getPricingGridColumns, {
  filterPwbHiddenColumns
} from "../../../helpers/getPricingGridColumns";
import LanesGridBody from "./LanesGridBody";
import getModeForUserRole from "../../../helpers/getModeForUserRole";
import messages from "../../../helpers/messages";
import { isSpecialPaste } from "../../../helpers/handleSpecialPastingInGrid";
import { disableMatchingLanes } from "../../../slices/laneMatchSlice";
import getCurrency from "../../../helpers/getCurrency";
import tlColumnConstants from "../../../helpers/tlColumnConstants";

const {
  STRATEGIC_QUANTILE_TTT_COST,
  STRATEGIC_QUANTILE,
  PWB_COST,
  PRICING_MODEL
} = tlColumnConstants;

class LanesGrid extends Component {
  adjustTopScroll = () => {
    const table = document.getElementById("pricing-grid");
    const topChild = document.getElementById("top-scroll-div-content");
    if (topChild && table) {
      topChild.style.width = table.offsetWidth + "px";
    }
  };

  componentDidUpdate() {
    this.adjustTopScroll();
  }

  render() {
    const {
      dispatch,
      role,
      division: userDivision,
      bidId,
      bidPricing: {
        lanes,
        lanesLoading: loading,
        lanesError: error,
        averageMargin,
        totalFiltered,
        offset,
        perPage,
        syncOn,
        columns: { order: columnOrder },
        sortField,
        sortOrder,
        lanesLoading,
        columnFilterMenu,
        columnFilters,
        rangeFilters,
        columnWidths,
        useRxoTlServerPagedLanes,
        isLaneImportInProgress
      },
      summary,
      laneMatch: { matchingLaneRequestLaneId },
      featureFlags: { useStrategicQuantile, hidePwbCost },
      strategicQuantile: {
        strategicQuantile: { minimumValue, maximumValue }
      }
    } = this.props;
    const division = this.props.bidPricing?.division || this.props.division;
    let columns = getPricingGridColumns(role, division, null, summary?.isCoyoteBid);
    let newColumnOrder = columnOrder;
    if (useStrategicQuantile === false || role === userRole.salesRep) {
      columns = columns.filter(
        x =>
          x.field !== STRATEGIC_QUANTILE_TTT_COST &&
          x.field !== STRATEGIC_QUANTILE
      );
      newColumnOrder = newColumnOrder.filter(
        x => x !== STRATEGIC_QUANTILE_TTT_COST && x !== STRATEGIC_QUANTILE
      );
    } else {
      let strategicQuantileColumn = columns.find(
        x => x.field === STRATEGIC_QUANTILE
      );
      strategicQuantileColumn = {
        ...strategicQuantileColumn,
        minValue: minimumValue,
        maxValue: maximumValue
      };
      const index = findIndex(columns, { field: STRATEGIC_QUANTILE });
      columns[index] = { ...strategicQuantileColumn };
    }

    if (hidePwbCost) {
      columns = filterPwbHiddenColumns(columns, hidePwbCost);
      newColumnOrder = newColumnOrder.filter(
        field => field !== PWB_COST && field !== PRICING_MODEL
      );
    }

    const tlView =
      role === userRole.tlPricingAnalyst ||
      role === userRole.salesRep ||
      role === userRole.assetBasedPricingUser;
    const unsavedChanges =
      lanes && lanes.length && lanes.some(l => l.metaData.laneChanged === true)
        ? true
        : false;
    const handleScroll = ({ target }, scrollDivId) =>
      (document.getElementById(scrollDivId).scrollLeft = target.scrollLeft);

    const mode = getModeForUserRole(role);
    const disableDeleteButton =
      summary?.modes.some(
        x => x.mode === mode && x.status >= status.submitted
      ) || isLaneImportInProgress;
    const disableRepriceButton = summary?.modes.some(
      x => x.mode === modeTypes.tl && x.status > status.submitted
    );

    const handlePaste = async (
      event,
      rowIndex,
      numberOfRowsRendered,
      cellDetails
    ) => {
      event.persist();

      const text = await navigator.clipboard.readText();
      const specialPaste =
        text && isSpecialPaste(text, rowIndex, numberOfRowsRendered);

      if (text && specialPaste.isValid) {
        event.preventDefault();
        dispatch(
          handleSpecialPaste({
            textArray: specialPaste.textArray,
            range: specialPaste.range,
            role,
            field: cellDetails.field
          })
        );
        dispatch(disableMatchingLanes());
      }
    };

    return (
      <div className="table-width" data-testid="pricing-grid">
        <Toolbar
          bidId={bidId}
          error={error}
          role={role}
          division={division}
          columns={columns}
          unsavedChanges={unsavedChanges}
          disableDeleteButton={disableDeleteButton}
          hasDisabledLanes={lanes?.some(l => l.metaData.disabled)}
          disableRepriceButton={disableRepriceButton}
        />

        {error && (
          <div
            style={{
              height:
                (role === userRole.salesRep ||
                  role === userRole.tlPricingAnalyst ||
                  role === userRole.assetBasedPricingUser) &&
                !error
                  ? undefined
                  : window.screen.height - 290
            }}
            className="panel"
          >
            <div className="m-0">
              <PanelStateIndicator
                error={error}
                errorMessage={messages.pleaseRefreshThePage.defaultMessage}
              />
            </div>
          </div>
        )}

        {error || (
          <>
            <div
              onScroll={e => handleScroll(e, "bottom-scroll-div")}
              className="overflow-x--scroll scroll h-12"
              id="top-scroll-div"
            >
              <div id="top-scroll-div-content">&nbsp;</div>
            </div>
            <div
              onScroll={e => handleScroll(e, "top-scroll-div")}
              id="bottom-scroll-div"
              style={{ minHeight: window.screen.height - 240 }}
              className="overflow-x--scroll scroll"
            >
              <table id="pricing-grid" className="bg-white pl-16 pos-relative">
                <PricingGridHeader
                  columns={columns}
                  role={role}
                  bidId={bidId}
                  columnOrder={newColumnOrder}
                  adjustTopScroll={this.adjustTopScroll}
                  setColumnOrder={setColumnOrder}
                  toggleColumnFilterMenu={
                    useRxoTlServerPagedLanes
                      ? toggleRxoTlColumnFilterMenu
                      : toggleColumnFilterMenu
                  }
                  filterPricingLanes={filterPricingLanes}
                  setColumnWidth={setColumnWidth}
                  sortPricingLanes={sortPricingLanes}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  lanesLoading={lanesLoading}
                  columnFilterMenu={columnFilterMenu}
                  columnFilters={columnFilters}
                  rangeFilters={rangeFilters}
                  columnWidths={columnWidths}
                  useRxoTlServerPagedLanes={useRxoTlServerPagedLanes}
                />

                {loading && (
                  <tbody>
                    {times(tlView ? 16 : 22, key => (
                      <tr className="row loading-row" key={key}>
                        {times(newColumnOrder.length, index => (
                          <th
                            key={index}
                            className="content-placeholder loading-cell"
                          >
                            <span></span>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )}

                {!loading && lanes && (
                  <LanesGridBody
                    columns={columns}
                    lanes={lanes}
                    offset={offset}
                    perPage={perPage}
                    role={role}
                    columnOrder={newColumnOrder}
                    disableDeleteButton={disableDeleteButton}
                    matchingLaneRequestLaneId={matchingLaneRequestLaneId}
                    handlePaste={handlePaste}
                    bidDivision={division}
                    userDivision={userDivision}
                    isCoyoteBid={summary?.isCoyoteBid}
                    isServerPaged={useRxoTlServerPagedLanes}
                  />
                )}
              </table>
            </div>
          </>
        )}

        <div className="grid bg-white row border-top-grey">
          {!loading && !error && (
            <div className="grid__cell one-whole p-12">
              <span data-testid="total-lanes">
                <FormattedMessage
                  {...messages.totalLane}
                  values={{ totalFiltered: totalFiltered }}
                />
              </span>
              <span data-testid="average-margin" className="pl-16">
                <FormattedMessage
                  {...messages.averageMargin}
                  values={{
                    averageMargin: `${getCurrency(division)}${averageMargin}`
                  }}
                />
              </span>
              <span data-testid="last-saved" className="fr">
                <FormattedMessage
                  {...messages.lastSaved}
                  values={{ syncOn: syncOn }}
                />
              </span>
            </div>
          )}
          {loading &&
            times(3, index => (
              <div
                key={index}
                className={
                  "grid__cell one-tenth content-placeholder loading-cell " +
                  (index === 2 && "fr")
                }
              >
                <span></span>
              </div>
            ))}
        </div>
        {!error && !loading && (
          <div className="one-whole p-20 fr">
            <Pagination
              onChange={paginationDetails => {
                dispatch(changePricingLanesPagination(paginationDetails));
                if (useRxoTlServerPagedLanes) {
                  dispatch(refetchRxoTlPricingPage({ bidId, role }));
                } else {
                  dispatch(getLaneMatchCountForTLLanes());
                }
              }}
              limit={perPage}
              offset={offset}
              total={totalFiltered}
              perPageOptions={[25, 50, 100, 200]}
            />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const {
    bidPricing,
    bidSummary: { summary },
    laneMatch,
    featureFlags,
    strategicQuantile
  } = state;
  return {
    bidPricing,
    summary,
    laneMatch,
    featureFlags,
    strategicQuantile
  };
};

export default connect(mapStateToProps)(LanesGrid);
